'use client';
import { useState } from 'react';
import { Container, Row, Col, Alert, Spinner } from 'react-bootstrap';
import { useIngredient } from '../../../apps/hooks/ingredient/useIngredient';
import { PageHeader } from './components/PageHeader';
import { IngredientList } from './components/IngredientList';
import { CreateIngredientModal } from './components/CreateIngredientModal';
import { UpdateIngredientModal } from './components/UpdateIngredientModal';
import { DeleteIngredientModal } from './components/DeleteIngredientModal';
import { CSVUploadModal } from './components/CSVUploadModal';
import type { Ingredient, CreateIngredientRequest, UpdateIngredientRequest } from '../../../models/Ingredient';

export default function IngredientPage() {
  const {
    ingredientsQuery,
    createIngredient,
    updateIngredient,
    deleteIngredient,
    bulkCreateIngredients,
    isCreating,
    isUpdating,
    isDeleting,
    isBulkCreating,
    error,
    setError,
  } = useIngredient();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCSVModal, setShowCSVModal] = useState(false);
  const [selectedIngredient, setSelectedIngredient] = useState<Ingredient | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const handleCreateNew = () => {
    setShowCreateModal(true);
  };

  const handleCreateIngredient = async (data: CreateIngredientRequest) => {
    try {
      await createIngredient(data);
      setShowCreateModal(false);
      setSuccessMessage('Ingredient created successfully!');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error('Failed to create ingredient:', error);
    }
  };

  const handleEditIngredient = (ingredient: Ingredient) => {
    setSelectedIngredient(ingredient);
    setShowUpdateModal(true);
  };

  const handleUpdateIngredient = async (data: UpdateIngredientRequest) => {
    if (!selectedIngredient) {
      return;
    }

    try {
      await updateIngredient({
        ingredientName: selectedIngredient.ingredientName,
        request: data,
      });
      setShowUpdateModal(false);
      setSelectedIngredient(null);
      setSuccessMessage('Ingredient updated successfully!');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error('Failed to update ingredient:', error);
    }
  };

  const handleDeleteIngredient = (ingredient: Ingredient) => {
    setSelectedIngredient(ingredient);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedIngredient) {
      return;
    }

    try {
      await deleteIngredient(selectedIngredient.ingredientName);
      setShowDeleteModal(false);
      setSelectedIngredient(null);
      setSuccessMessage('Ingredient deleted successfully!');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error('Failed to delete ingredient:', error);
    }
  };

  const handleCSVUpload = async (ingredients: CreateIngredientRequest[]) => {
    try {
      const result = await bulkCreateIngredients(ingredients);
      setShowCSVModal(false);
      if (result.successful > 0) {
        setSuccessMessage(
          `Successfully uploaded ${result.successful} ingredients!${result.failed > 0 ? ` ${result.failed} failed.` : ''}`,
        );
        setTimeout(() => setSuccessMessage(null), 5000);
      }
      if (result.errors.length > 0) {
        console.error('Upload errors:', result.errors);
      }
    } catch (error) {
      console.error('Failed to upload ingredients:', error);
    }
  };

  const handleCloseModals = () => {
    setShowCreateModal(false);
    setShowUpdateModal(false);
    setShowDeleteModal(false);
    setShowCSVModal(false);
    setSelectedIngredient(null);
    setError(null);
  };

  return (
    <Container fluid className="py-4">
      <PageHeader
        title="Ingredient Management"
        onCreateNew={handleCreateNew}
        onCSVUpload={() => setShowCSVModal(true)}
      />

      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert variant="success" onClose={() => setSuccessMessage(null)} dismissible>
          {successMessage}
        </Alert>
      )}

      <Row>
        <Col>
          {ingredientsQuery.isLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            </div>
          ) : (
            <IngredientList
              ingredients={ingredientsQuery.data?.ingredients || []}
              onEdit={handleEditIngredient}
              onDelete={handleDeleteIngredient}
              loading={ingredientsQuery.isLoading}
            />
          )}
        </Col>
      </Row>

      <CreateIngredientModal
        show={showCreateModal}
        onHide={handleCloseModals}
        onSubmit={handleCreateIngredient}
        isSubmitting={isCreating}
      />

      <UpdateIngredientModal
        show={showUpdateModal}
        ingredient={selectedIngredient}
        onHide={handleCloseModals}
        onSubmit={handleUpdateIngredient}
        isSubmitting={isUpdating}
      />

      <DeleteIngredientModal
        show={showDeleteModal}
        ingredient={selectedIngredient}
        onHide={handleCloseModals}
        onConfirm={handleConfirmDelete}
        isDeleting={isDeleting}
      />

      <CSVUploadModal
        show={showCSVModal}
        onHide={handleCloseModals}
        onUpload={handleCSVUpload}
        isUploading={isBulkCreating}
      />
    </Container>
  );
}
